import { Component, computed, ElementRef, OnInit, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CLIENT_ID } from '@environments/environment';
import { debounceTime, map, tap } from 'rxjs';
import { StrapsModalComponent } from 'src/app/components/modals/straps-modal/straps-modal.component';
import { Products } from 'src/app/models/types/Product/Product.type';
import { AutocompleteDTO, AutocompleteValue } from 'src/app/models/types/ProductsSearch/AutocompleteDTO.type copy';
import { SearchDTO, SearchResultItem } from 'src/app/models/types/ProductsSearch/SearchDTO.type';
import { SharedModule } from 'src/app/modules/shared.module';
import { ProductsSearchService } from 'src/app/services/api/products-search.service';
import { StockAndPriceService } from 'src/app/services/api/stock-and-price.service';
import { LoaderService } from 'src/app/services/loader.service';
import { SharedService } from 'src/app/services/shared.service';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { ModalComponent } from '../modal/modal.component';
import { BuildEndPayload, StrapDTO } from 'src/app/models/types/Strap/Strap.type';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-search-page',
  imports: [
    SharedModule,
    BreadcrumbComponent,
    ModalComponent,
    StrapsModalComponent
  ],
  templateUrl: './search-page.component.html',
  styleUrl: './search-page.component.scss'
})
export class SearchPageComponent implements OnInit {

  @ViewChild('input') inputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('modal', { static: false }) modal!: ModalComponent;
  @ViewChild('strapsModal', { static: false }) strapsModal!: StrapsModalComponent;

  private readonly SUGGESTIONS_STORAGE_KEY = `app.suggestions.${CLIENT_ID}`;

  private _suggestions: WritableSignal<AutocompleteValue[]> = signal([]);
  public suggestions: Signal<AutocompleteValue[]> = this._suggestions.asReadonly();

  private _products: WritableSignal<Products> = signal([]);
  public products: Signal<Products> = this._products.asReadonly();

  public hint: WritableSignal<AutocompleteValue | null> = signal(null);

  public errorMessage: WritableSignal<string | null> = signal(null);

  public form!: FormGroup;
  public strapsForm!: FormGroup;
  // public loading: WritableSignal<boolean> = signal(false);
  public loading: WritableSignal<boolean> = signal(false);
  public loadingEffect = computed(() => {
    let loading = this.loading();
    if (loading) {
      this.strapsForm.disable();
    } else {
      this.strapsForm.enable();
    }
  });

  public get disabledSearch(): boolean {
    return this.strapsForm.invalid || !this.strapsForm.dirty || this.loading();
  }

  public get disabledStrapSearch(): boolean {
    return this.loading();
  }

  constructor(
    public readonly authService: AuthService,
    private readonly productsSearchService: ProductsSearchService,
    private readonly loaderService: LoaderService,
    private readonly sharedService: SharedService,
    private readonly fb: FormBuilder,
  ) {
    this.form = this.fb.group({
      search: ''
    });
    this.strapsForm = this.fb.group({
      search: ''
    });
    this.loading.set(false);
  }

  ngOnInit(): void {
    this.updateSuggestionsFromLocalStorage();
    this.form.valueChanges
      .pipe(
        tap(() => this.loading.set(true)),
        debounceTime(500),
        map(value => {
          return value.search;
        }),
        tap(() => this.loading.set(false))
      )
      .subscribe((search) => {
        if (search) {
          this.handleAutocompleteSearch(search);
          this.handleSearch(search);
        } else {
          this._products.set([]);
          this.hint.set(null);
        }
      });
  }

  handleSubmit(event: Event): void {
    event.preventDefault();
    const searchValue = this.form.value;
  }

  handleReset(): void {
    this.form.setValue({ search: '' });
  }

  handleAutocompleteSearch(searchText: string): void {
    this.loaderService.show();
    this.productsSearchService.autocomplete(searchText || '').subscribe({
      next: (response: AutocompleteDTO) => {
        // this.updateSuggestions(response.value)
        this.hint.set(response.value.length > 0 ? response.value[0] : null);
        this.loaderService.hide();
      },
      error: (error: any) => {
        console.error('Search error:', error);
        this.loaderService.hide();
      }
    });
  }

  handleSearch(searchText: string): void {
    this.loaderService.show();
    this.productsSearchService.searchProduct(searchText || '').subscribe({
      next: (response: SearchDTO) => {
        this._products.set(this.parseToProducts(response.value));
        this.loaderService.hide();
      },
      error: (error: any) => {
        console.error('Search error:', error);
        this.loaderService.hide();
      }
    });
  }

  parseToProducts(searchResultItems: SearchResultItem[]): Products {
    return searchResultItems.map(item => ({
      sapCode: item.SAPCode,
      catalogCode: item.CatalogCode,
      media: item.Media || '',
      canShowPrice: item.CanShowPrice,
      isNewIn: item.IsNewIn,
      lowPriorityJewelry: item.LowPriorityJewelry,
      classification: item.classification,
      // aggiungi qui altri campi necessari in base alla definizione di Products e SearchResultItem
    }));
  }

  selectSuggestion(suggestion: AutocompleteValue): void {
    this.form.setValue({ search: suggestion.text });
  }

  updateSuggestions(suggestions: AutocompleteValue[]) {
    // Rimuovi tutte le suggestions nuove già presenti
    let existing = this._suggestions().filter(e => !suggestions.some(s => s.text === e.text));
    // Inserisci le nuove suggestions all'inizio
    this._suggestions.set([...suggestions, ...existing]);
    this.saveSuggestionsInLocalStorage();
  }

  saveSuggestionsInLocalStorage() {
    let suggestions = this._suggestions();
    localStorage.setItem(this.SUGGESTIONS_STORAGE_KEY, JSON.stringify(suggestions));
  }

  updateSuggestionsFromLocalStorage() {
    let obj = localStorage.getItem(this.SUGGESTIONS_STORAGE_KEY);
    if (obj) {
      let suggestions: AutocompleteValue[] = JSON.parse(obj);
      this._suggestions.set(suggestions);
    }
  }

  handleRemoveSuggestion(text: string) {
    let suggestion = this._suggestions().find(s => s.text === text);
    if (suggestion) {
      this._suggestions.set(this._suggestions().filter(s => s.text !== text));
      this.saveSuggestionsInLocalStorage();
    }
  }

  handleProductClick(sapCode: any): void {
    if (this.hint()) {
      this.updateSuggestions([{ queryPlusText: this.form.value.search, text: this.form.value.search }]);
    }
    this.sharedService.navigateTo_ProductPage(sapCode);
  }

  handleSearchStraps(): void {
    this.modal.buildModal(() => { this.dismissSearchStrapsModal() }, 'md', 'seach-strap-modal');
    setTimeout(() => {
      this.inputRef?.nativeElement.focus();
    }, 500);
  }

  searchStraps(): void {
    this.loading.set(true);
    this.strapsModal.openModal(this.strapsForm.value.search);
    this.errorMessage.set(null);
  }

  dismissSearchStrapsModal(): void {
    this.strapsForm.setValue({ search: '' });
    this.errorMessage.set(null);
    this.modal.dismiss();
  }

  handleStrapsModalBuildEnd(data: BuildEndPayload) {
    if (data.success) {
      this.dismissSearchStrapsModal();
    } else {
      this.errorMessage.set(data.message || 'An error occurred');
    }
    this.loading.set(false);
    this.inputRef?.nativeElement.focus();
  }
}
